import { zodResolver } from '@hookform/resolvers/zod';
import { MoveLeft, MoveRight } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AuthShell } from '@/components/layout/AuthShell';
import { YellowUnderline } from '@/components/patterns/YellowUnderline';

import { MarketingCarousel } from '../components/MarketingCarousel';
import { OnboardingStepper } from '../components/OnboardingStepper';
import { type WorkspaceInfoInput, workspaceInfoSchema } from '../schemas/onboardingSchemas';
import { useOnboardingStore } from '../store/onboardingStore';

// /onboarding/workspace-info — Step 2 of the 3-screen onboarding flow.
//
// Optional analytics fields only: industry and team size. Both can be skipped.
// Organization name was moved to step 1 (YourDetailsScreen) to match the
// auth-frontend OnboardingDetailsPage layout. No API call on this screen —
// data accumulates in the store for step 3.
export function WorkspaceInfoScreen() {
  const navigate = useNavigate();
  const draft = useOnboardingStore((s) => s.workspaceInfo);
  const setDraft = useOnboardingStore((s) => s.setWorkspaceInfo);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<WorkspaceInfoInput>({
    resolver: zodResolver(workspaceInfoSchema),
    defaultValues: {
      industry: draft.industry ?? '',
      teamSize: draft.teamSize ?? '',
    },
  });

  useEffect(() => {
    const subscription = watch((values) => {
      setDraft({
        industry: values.industry ?? '',
        teamSize: values.teamSize ?? '',
      });
    });

    return () => subscription.unsubscribe();
  }, [setDraft, watch]);

  const onSubmit = handleSubmit((values) => {
    setDraft(values);
    navigate('/onboarding/welcome');
  });

  return (
    <AuthShell
      marketing={<MarketingCarousel />}
      footer={
        <div className="w-full flex items-center justify-between flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/onboarding/your-details')}
            className="btn btn-primary-light rounded-full!"
          >
            <MoveLeft className="w-6 h-6 shrink-0" />
            <span>Back</span>
          </button>
          <button
            type="submit"
            form="workspace-info-form"
            className="btn btn-primary rounded-full!"
          >
            <span>Next</span>
            <MoveRight className="w-6 h-6 shrink-0" />
          </button>
        </div>
      }
    >
      <form
        id="workspace-info-form"
        onSubmit={onSubmit}
        noValidate
        className="max-w-[460px] w-full shrink-0 flex flex-col gap-4 sm:gap-6 lg:gap-8"
      >
        <OnboardingStepper step={2} />

        <div className="flex flex-col gap-1 sm:gap-2 lg:gap-2.5 text-center">
          <h1 className="text-xl lg:text-2xl lg:leading-[29px] font-bold text-black-light">
            About Your <YellowUnderline>Organization</YellowUnderline>
          </h1>
          <p className="text-base lg:text-lg leading-[150%] font-medium">
            Help us tailor your experience (optional)
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="industry" className="sc-input-label">
                Industry
              </label>
              <select
                id="industry"
                className="sc-select has-[option:disabled:checked]:text-placeholder"
                defaultValue=""
                aria-invalid={errors.industry ? 'true' : undefined}
                {...register('industry')}
              >
                <option value="" disabled hidden>
                  Select industry
                </option>
                <option value="Information and Technology">Information and Technology</option>
                <option value="Pharma">Pharma</option>
                <option value="Finance">Finance</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Education">Education</option>
                <option value="Retail">Retail</option>
                <option value="Other">Other</option>
              </select>
              {errors.industry && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.industry.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="team-size" className="sc-input-label">
                Team Size
              </label>
              <select
                id="team-size"
                className="sc-select has-[option:disabled:checked]:text-placeholder"
                defaultValue=""
                aria-invalid={errors.teamSize ? 'true' : undefined}
                {...register('teamSize')}
              >
                <option value="" disabled hidden>
                  Select team size
                </option>
                <option value="0-10">0–10</option>
                <option value="10-20">10–20</option>
                <option value="20-30">20–30</option>
                <option value="30+">30+</option>
              </select>
              {errors.teamSize && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.teamSize.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>
    </AuthShell>
  );
}
